// src/agenda/state.js
// Agenda state management with localStorage persistence and basic migration
import { useCallback, useEffect, useMemo, useReducer } from 'react';

const STORAGE_KEY = 'agenda@v1';

const DAY_MAP_STR_TO_NUM = { lu: 1, ma: 2, mi: 3, ju: 4, vi: 5, sa: 6 };

/**
 * @typedef {Object} Term
 * @property {string} id
 * @property {string} nombre
 * @property {string} fechaInicio // ISO yyyy-MM-dd
 * @property {string} fechaFin    // ISO yyyy-MM-dd
 */

/**
 * @typedef {Object} MateriaRef
 * @property {string} codigo
 * @property {string} nombre
 * @property {('CPC'|'CPO')} plan
 */

/**
 * @typedef {Object} Cursada
 * @property {string} id
 * @property {string} termId
 * @property {MateriaRef=} materia
 * @property {Array<1|2|3|4|5|6>} dias  // 1=Lu ... 6=Sá
 * @property {string} horaInicio // HH:mm
 * @property {string} horaFin    // HH:mm
 * @property {string=} aula
 * @property {string=} profesor
 * @property {string=} color
 * @property {string=} notas
 */

/**
 * @typedef {Object} Evento
 * @property {string} id
 * @property {string} termId
 * @property {('Parcial'|'Final'|'TP'|'Exposición'|'Otro')} tipo
 * @property {string} titulo
 * @property {string} fecha // ISO yyyy-MM-dd
 * @property {string=} horaInicio // HH:mm
 * @property {string=} horaFin    // HH:mm
 * @property {MateriaRef=} materia
 * @property {string=} ubicacion
 * @property {string=} color
 * @property {string=} notas
 */

/**
 * @typedef {Object} AgendaState
 * @property {number} version
 * @property {string|null} activeTermId
 * @property {Term[]} terms
 * @property {Cursada[]} cursadas
 * @property {Evento[]} eventos
 */

/**
 * @returns {AgendaState}
 */
function migrateLegacyKeys() {
  try {
    const legacyEvents = localStorage.getItem('agendaEstudio');
    const legacyCursadas = localStorage.getItem('cursadasRecurrentes');
    /** @type {AgendaState} */
    const migrated = { version: 1, activeTermId: null, terms: [], cursadas: [], eventos: [] };
    if (legacyEvents) {
      const parsed = JSON.parse(legacyEvents);
      for (const ev of Array.isArray(parsed) ? parsed : []) {
        const start = ev.start ? new Date(ev.start).toISOString() : null;
        const end = ev.end ? new Date(ev.end).toISOString() : start;
        if (start && end) {
          migrated.eventos.push({
            id: String(ev.id ?? Date.now() + Math.random()),
            termId: migrated.activeTermId,
            tipo: 'Otro',
            titulo: ev.title || 'Evento',
            fecha: start.slice(0, 10),
            horaInicio: start.slice(11, 16),
            horaFin: end.slice(11, 16),
            color: ev.resource?.color,
          });
        }
      }
    }
    if (legacyCursadas) {
      const parsed = JSON.parse(legacyCursadas);
      for (const c of Array.isArray(parsed) ? parsed : []) {
        migrated.cursadas.push({
          id: String(c.id ?? Date.now() + Math.random()),
          termId: migrated.activeTermId,
          materia: c.materia || 'Cursada',
          dias: (c.dias || []).map((d) => {
            // legacy stored as spanish strings: 'lunes', 'martes', ...
            const map = { lunes: 'lu', martes: 'ma', miércoles: 'mi', jueves: 'ju', viernes: 'vi', sábado: 'sa' };
            return map[d] || 'lu';
          }),
          horaInicio: c.horaInicio,
          horaFin: c.horaFin,
          desde: new Date().toISOString().slice(0, 10),
          hasta: new Date(Date.now() + 1000 * 60 * 60 * 24 * 90).toISOString().slice(0, 10),
          aula: c.aula,
          color: c.color,
        });
      }
    }
    if (migrated.eventos.length || migrated.cursadas.length) {
      localStorage.removeItem('agendaEstudio');
      localStorage.removeItem('cursadasRecurrentes');
      return migrated;
    }
  } catch {}
  return null;
}

function getInitialState() {
  const empty = {
    version: 1,
    activeTermId: null,
    terms: [],
    cursadas: [],
    eventos: [],
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw);
    // Basic migration guard
    if (!parsed || typeof parsed !== 'object') return empty;
    if (!parsed.version) parsed.version = 1;
    return /** @type {AgendaState} */({
      version: 1,
      activeTermId: parsed.activeTermId ?? null,
      terms: Array.isArray(parsed.terms) ? parsed.terms : [],
      cursadas: Array.isArray(parsed.cursadas) ? parsed.cursadas : [],
      eventos: Array.isArray(parsed.eventos) ? parsed.eventos : [],
    });
  } catch {
    // Try legacy migration if parse failed
    const migrated = migrateLegacyKeys();
    return migrated || empty;
  }
}

function persist(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

const ACTIONS = {
  SET_STATE: 'SET_STATE',
  CLEAR: 'CLEAR',
  SET_ACTIVE_TERM: 'SET_ACTIVE_TERM',
  ADD_TERM: 'ADD_TERM',
  UPDATE_TERM: 'UPDATE_TERM',
  REMOVE_TERM: 'REMOVE_TERM',
  ADD_CURSADA: 'ADD_CURSADA',
  UPDATE_CURSADA: 'UPDATE_CURSADA',
  REMOVE_CURSADA: 'REMOVE_CURSADA',
  ADD_EVENTO: 'ADD_EVENTO',
  UPDATE_EVENTO: 'UPDATE_EVENTO',
  REMOVE_EVENTO: 'REMOVE_EVENTO',
};

/**
 * @param {AgendaState} state
 * @param {{type:string, payload:any}} action
 * @returns {AgendaState}
 */
function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_STATE:
      return { ...state, ...action.payload };
    case ACTIONS.CLEAR:
      return { version: (state.version || 1) + 1, activeTermId: null, terms: [], cursadas: [], eventos: [] };
    case ACTIONS.SET_ACTIVE_TERM:
      return { ...state, activeTermId: action.payload };
    case ACTIONS.ADD_TERM:
      return { ...state, version: (state.version || 1) + 1, terms: [...state.terms, action.payload] };
    case ACTIONS.UPDATE_TERM:
      return {
        ...state,
        version: (state.version || 1) + 1,
        terms: state.terms.map((t) => (t.id === action.payload.id ? { ...t, ...action.payload } : t)),
      };
    case ACTIONS.REMOVE_TERM: {
      const filtered = state.terms.filter((t) => t.id !== action.payload);
      const newActive = state.activeTermId === action.payload ? null : state.activeTermId;
      return { ...state, version: (state.version || 1) + 1, terms: filtered, activeTermId: newActive };
    }
    case ACTIONS.ADD_CURSADA:
      return { ...state, version: (state.version || 1) + 1, cursadas: [...state.cursadas, action.payload] };
    case ACTIONS.UPDATE_CURSADA:
      return {
        ...state,
        version: (state.version || 1) + 1,
        cursadas: state.cursadas.map((c) => (c.id === action.payload.id ? { ...c, ...action.payload } : c)),
      };
    case ACTIONS.REMOVE_CURSADA:
      return { ...state, version: (state.version || 1) + 1, cursadas: state.cursadas.filter((c) => c.id !== action.payload) };
    case ACTIONS.ADD_EVENTO:
      return { ...state, version: (state.version || 1) + 1, eventos: [...state.eventos, action.payload] };
    case ACTIONS.UPDATE_EVENTO:
      return {
        ...state,
        version: (state.version || 1) + 1,
        eventos: state.eventos.map((e) => (e.id === action.payload.id ? { ...e, ...action.payload } : e)),
      };
    case ACTIONS.REMOVE_EVENTO:
      return { ...state, version: (state.version || 1) + 1, eventos: state.eventos.filter((e) => e.id !== action.payload) };
    default:
      return state;
  }
}

export function useAgenda() {
  const [state, dispatch] = useReducer(reducer, undefined, getInitialState);

  useEffect(() => {
    persist(state);
  }, [state]);

  const setActiveTerm = useCallback((id) => dispatch({ type: ACTIONS.SET_ACTIVE_TERM, payload: id }), []);
  const addTerm = useCallback((term) => dispatch({ type: ACTIONS.ADD_TERM, payload: term }), []);
  const updateTerm = useCallback((term) => dispatch({ type: ACTIONS.UPDATE_TERM, payload: term }), []);
  const removeTerm = useCallback((id) => dispatch({ type: ACTIONS.REMOVE_TERM, payload: id }), []);

  const addCursada = useCallback((c) => dispatch({ type: ACTIONS.ADD_CURSADA, payload: c }), []);
  const updateCursada = useCallback((c) => dispatch({ type: ACTIONS.UPDATE_CURSADA, payload: c }), []);
  const removeCursada = useCallback((id) => dispatch({ type: ACTIONS.REMOVE_CURSADA, payload: id }), []);

  const addEvento = useCallback((e) => dispatch({ type: ACTIONS.ADD_EVENTO, payload: e }), []);
  const updateEvento = useCallback((e) => dispatch({ type: ACTIONS.UPDATE_EVENTO, payload: e }), []);
  const removeEvento = useCallback((id) => dispatch({ type: ACTIONS.REMOVE_EVENTO, payload: id }), []);
  const clearAll = useCallback(() => dispatch({ type: ACTIONS.CLEAR }), []);

  const activeTerm = useMemo(
    () => state.terms.find((t) => t.id === state.activeTermId) || null,
    [state.terms, state.activeTermId]
  );

  return {
    state,
    activeTerm,
    setActiveTerm,
    addTerm,
    updateTerm,
    removeTerm,
    addCursada,
    updateCursada,
    removeCursada,
    addEvento,
    updateEvento,
    removeEvento,
    clearAll,
    // selectors
    selectCalendarEvents: (termId) => selectAllAsCalendarEvents(state, termId || state.activeTermId),
    detectConflicts: () => detectConflicts(state, state.activeTermId),
  };
}

// Selectors
export function selectAllAsCalendarEvents(state, termId) {
  const usingTerm = Boolean(termId);
  const term = usingTerm ? state.terms.find((t) => t.id === termId) : null;
  const events = [];
  // eventos únicos
  for (const e of state.eventos.filter((x) => (usingTerm ? x.termId === termId : true))) {
    const start = e.start || (e.fecha && e.horaInicio ? `${e.fecha}T${e.horaInicio}:00` : `${e.fecha}T00:00:00`);
    const end = e.end || (e.fecha && e.horaFin ? `${e.fecha}T${e.horaFin}:00` : start);
    const color = e.color || colorByTipoSafe(e.tipo);
    events.push({ id: e.id, title: `${e.tipo}: ${e.titulo}`, start, end, extendedProps: { kind: 'evento' }, borderColor: color, backgroundColor: color });
  }
  // cursadas recurrentes como eventos recurrentes de FullCalendar
  for (const c of state.cursadas.filter((x) => (usingTerm ? x.termId === termId : true))) {
    if (c.visible === false) continue; // permitir ocultar cursadas sin borrarlas
    const dayNums = (c.dias || []).map((d) => (typeof d === 'string' ? DAY_MAP_STR_TO_NUM[d] : d)).filter(Boolean);
    // Mapear 1..6 (Lu..Sa) a 0..6 (FullCalendar, 0=Dom)
    const daysOfWeek = dayNums
      .map((n) => (n >= 1 && n <= 6 ? n : null))
      .filter((n) => n !== null);
    if (!daysOfWeek.length) continue;
    // Calcular startRecur / endRecur (exclusivo) y sumar 1 día a end
    const startRecur = (usingTerm && term ? term.fechaInicio : c.desde) || c.desde;
    const hasta = (usingTerm && term ? term.fechaFin : c.hasta) || c.hasta;
    const endDate = new Date(hasta);
    // Si endRecur igual a startRecur, sumar un día
    const startDate = new Date(startRecur);
    if (endDate.getTime() === startDate.getTime()) {
      endDate.setDate(endDate.getDate() + 1);
    } else {
      endDate.setDate(endDate.getDate() + 1); // endRecur exclusivo
    }
    const endRecur = new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    const startRecurISO = new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    const color = c.color || '#10b981';
    const startTime = (c.horaInicio || '00:00');
    const endTime = (c.horaFin || '00:00');
    events.push({
      id: c.id,
      title: `Cursada: ${c.materia}`,
      daysOfWeek,
      startTime,
      endTime,
      startRecur: startRecurISO,
      endRecur,
      display: 'auto',
      editable: false,
      extendedProps: { kind: 'cursada' },
      borderColor: color,
      backgroundColor: color,
    });
  }
  return events;
}

export function detectConflicts(state, termId) {
  const items = selectAllAsCalendarEvents(state, termId);
  const parse = (x) => ({ s: new Date(x.start).getTime(), e: new Date(x.end).getTime(), id: x.id });
  const segments = items.map(parse);
  const conflicts = new Set();
  for (let i = 0; i < segments.length; i++) {
    for (let j = i + 1; j < segments.length; j++) {
      const a = segments[i], b = segments[j];
      if (a.s < b.e && b.s < a.e) {
        conflicts.add(items[i].id);
        conflicts.add(items[j].id);
      }
    }
  }
  return conflicts;
}

function colorByTipoSafe(tipo){
  switch(tipo){
    case 'Parcial': return '#f59e0b';
    case 'Final': return '#8b5cf6';
    case 'TP':
    case 'Exposición': return '#2563eb';
    default: return '#94a3b8';
  }
}

// Devuelve eventos únicos listos para FullCalendar
export function selectEventosUnicos(state) {
  const list = [];
  for (const e of state.eventos) {
    const start = e.start || (e.fecha && e.horaInicio ? `${e.fecha}T${e.horaInicio}:00` : `${e.fecha}T00:00:00`);
    let end = e.end || (e.fecha && e.horaFin ? `${e.fecha}T${e.horaFin}:00` : start);
    let allDay = false;
    if (e.tipo === 'Entrega de TP' && !e.horaInicio && !e.horaFin) {
      // deadline allDay
      const day = e.fecha || String(start).slice(0, 10);
      allDay = true;
      // FullCalendar allDay spans use date-only; end is exclusive, but for allDay single-day, omit end
      end = undefined;
      list.push({ id: e.id, title: `${e.titulo}`, start: day, allDay, extendedProps: { kind: 'evento', tipo: e.tipo } });
      continue;
    }
    const color = e.color || colorByTipoSafe(e.tipo);
    list.push({ id: e.id, title: `${e.tipo}: ${e.titulo}`, start, end, allDay, extendedProps: { kind: 'evento', tipo: e.tipo }, borderColor: color, backgroundColor: color });
  }
  return list;
}

// Expande cursadas en un rango [rangeStart, rangeEnd) y devuelve eventos listos para FC
export function expandCursadasEnRango(state, rangeStart, rangeEnd) {
  const items = [];
  const startMs = new Date(rangeStart).getTime();
  const endMs = new Date(rangeEnd).getTime();
  for (const c of state.cursadas) {
    if (c.visible === false) continue;
    const daysRaw = (c.dias || []).map((d) => (typeof d === 'string' ? DAY_MAP_STR_TO_NUM[d] : d)); // 1..6
    const allowedDays = new Set(daysRaw.filter((n) => n >= 1 && n <= 6));
    if (allowedDays.size === 0) continue;
    const from = new Date(c.desde);
    const to = c.hasta ? new Date(c.hasta) : new Date(from.getTime() + 12 * 7 * 24 * 3600 * 1000);
    // iterate day by day across the visible range
    const d = new Date(Math.max(startMs, from.getTime()));
    while (d.getTime() < Math.min(endMs, to.getTime() + 24 * 3600 * 1000)) {
      const weekday = ((d.getDay() + 6) % 7) + 1; // 1..7
      if (allowedDays.has(weekday)) {
        const [sh, sm] = (c.horaInicio || '00:00').split(':').map(Number);
        const [eh, em] = (c.horaFin || '00:00').split(':').map(Number);
        const s = new Date(d); s.setHours(sh, sm, 0, 0);
        const e = new Date(d); e.setHours(eh, em, 0, 0);
        const ymd = `${s.getFullYear()}${String(s.getMonth()+1).padStart(2,'0')}${String(s.getDate()).padStart(2,'0')}`;
        const color = c.color || '#10b981';
        items.push({ id: `${c.id}-${ymd}`, title: `Cursada: ${c.materia}`, start: s, end: e, display: 'block', extendedProps: { kind: 'cursada', tipo: 'Cursada' }, borderColor: color, backgroundColor: color });
      }
      d.setDate(d.getDate() + 1);
    }
  }
  return items;
}


